import { useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventFormSubmissionApi } from '../api/eventFormSubmissions';
import type { Draft } from '../types/publicForm.types';

const DRAFT_KEY_PREFIX = 'event_form_draft_';
const AUTOSAVE_DELAY = 2000; // 2 seconds

interface UseEventFormDraftOptions {
  eventSlug: string;
  formSlug: string;
  enabled?: boolean;
}

export const useEventFormDraft = ({ eventSlug, formSlug, enabled = true }: UseEventFormDraftOptions) => {
  console.log('🏁 useEventFormDraft initialized with:', {
    eventSlug,
    formSlug,
    enabled,
    willQueryDraft: enabled && !!eventSlug && !!formSlug
  });
  
  const queryClient = useQueryClient();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const pendingUpdatesRef = useRef<Record<string, any>>({});

  // Storage key for this form's draft token
  const DRAFT_STORAGE_KEY = `${DRAFT_KEY_PREFIX}${eventSlug}_${formSlug}`;

  // Get or create draft - simplified like useFormDraft
  const { data: draft, isLoading: isDraftLoading } = useQuery({
    queryKey: ['eventFormDraft', eventSlug, formSlug],
    queryFn: async () => {
      console.log('🔄 Draft query starting:', { eventSlug, formSlug });
      
      // Don't make API calls if required params are missing
      if (!eventSlug || !formSlug) {
        return null;
      }
      
      // Check localStorage for existing auth token
      const storedAuthToken = localStorage.getItem(DRAFT_STORAGE_KEY);
      console.log('🔑 Stored token found:', !!storedAuthToken);
      
      if (storedAuthToken) {
        try {
          // Try to retrieve existing draft
          console.log('📥 Fetching existing draft with token:', storedAuthToken);
          const existingDraft = await eventFormSubmissionApi.getDraft(eventSlug, formSlug, storedAuthToken);
          console.log('✅ Existing draft loaded:', existingDraft);
          return existingDraft;
        } catch (error: any) {
          // Draft not found or expired, create new one
          console.warn('⚠️ Could not retrieve draft, creating new one');
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      }
      
      // Create new draft
      console.log('🆕 Creating new draft');
      const newDraft = await eventFormSubmissionApi.createDraft(eventSlug, formSlug);
      // Store the backend-generated token (handle both camelCase and snake_case)
      const authToken = newDraft.authToken || (newDraft as any).auth_token;
      console.log('💾 New draft created with token:', authToken);
      localStorage.setItem(DRAFT_STORAGE_KEY, authToken);
      return newDraft;
    },
    enabled: enabled && !!eventSlug && !!formSlug,
    staleTime: Infinity, // Draft doesn't go stale
    retry: 3,
  });

  // Update draft mutation - simplified like useFormDraft
  const updateDraftMutation = useMutation({
    mutationFn: async (updates: { submission?: any; responses?: any }) => {
      if (!draft?.authToken) throw new Error('No draft available');
      console.log('🚀 Sending updates to server with token:', draft.authToken);
      return eventFormSubmissionApi.updateDraft(eventSlug, formSlug, draft.authToken, updates);
    },
    onSuccess: (updatedDraft) => {
      console.log('✅ Draft saved successfully');
      queryClient.setQueryData(['eventFormDraft', eventSlug, formSlug], updatedDraft);
    },
    onError: (error: any) => {
      console.error('❌ Error saving draft:', error);
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Debounced save function - using setTimeout like useFormDraft
  const saveChanges = useCallback((updates: { submission?: any; responses?: any }) => {
    console.log('🎯 saveChanges called with:', {
      updates,
      hasAuthToken: !!draft?.authToken
    });
    
    if (!draft?.authToken) {
      console.warn('❌ No auth token available, skipping save');
      return;
    }

    // Merge with pending updates
    pendingUpdatesRef.current = {
      ...pendingUpdatesRef.current,
      ...updates,
      responses: {
        ...pendingUpdatesRef.current.responses,
        ...updates.responses,
      },
      submission: {
        ...pendingUpdatesRef.current.submission,
        ...updates.submission,
      },
    };

    console.log('📋 Pending updates merged:', pendingUpdatesRef.current);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      console.log('⏱️ Cleared previous save timeout');
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      console.log('⏰ Save timeout triggered after 2 seconds');
      const updatesToSave = { ...pendingUpdatesRef.current };
      pendingUpdatesRef.current = {};
      
      console.log('💾 Saving updates:', updatesToSave);
      updateDraftMutation.mutate(updatesToSave);
    }, AUTOSAVE_DELAY);
    
    console.log('⏱️ Save scheduled for 2 seconds from now');
  }, [draft?.authToken, updateDraftMutation]);

  // Save immediately (bypasses debouncing) - simplified like useFormDraft
  const saveNow = useCallback(async () => {
    if (!draft?.authToken || Object.keys(pendingUpdatesRef.current).length === 0) {
      console.log('⚠️ SaveNow: No auth token or no pending updates');
      return;
    }

    // Clear timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      console.log('⏱️ SaveNow: Cleared timeout');
    }

    // Save pending updates
    const updatesToSave = { ...pendingUpdatesRef.current };
    pendingUpdatesRef.current = {};
    
    console.log('💾 SaveNow: Saving updates immediately:', updatesToSave);
    await updateDraftMutation.mutateAsync(updatesToSave);
  }, [draft?.authToken, updateDraftMutation]);

  // Clear draft from storage
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    queryClient.removeQueries({ queryKey: ['eventFormDraft', eventSlug, formSlug] });
    console.log('🗑️ Draft cleared');
  }, [DRAFT_STORAGE_KEY, eventSlug, formSlug, queryClient]);

  // Save on unmount - simplified like useFormDraft
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        console.log('🧹 Cleanup: Cleared save timeout');
        
        // Save synchronously on unmount if possible
        if (pendingUpdatesRef.current && Object.keys(pendingUpdatesRef.current).length > 0 && draft?.authToken) {
          console.log('🚨 Cleanup: Sending pending updates via sendBeacon');
          navigator.sendBeacon(
            `/api/portal/events/${eventSlug}/forms/${formSlug}/drafts/${draft.authToken}`,
            JSON.stringify(pendingUpdatesRef.current)
          );
        }
      }
    };
  }, [eventSlug, formSlug, draft?.authToken]);

  return {
    draft,
    isDraftLoading,
    isSaving: updateDraftMutation.isPending,
    lastSaved: updateDraftMutation.isSuccess ? new Date() : null,
    saveError: updateDraftMutation.error,
    saveChanges,
    saveNow,
    clearDraft,
  };
};